import { CommonUtility } from '@utility/common';
import { ArrowRight } from '@phosphor-icons/react';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  CheckboxFormField,
  CustomField,
  DropdownFormField,
  MuteLabel,
} from '@elements/FormField';
import { useEffect, useState, useMemo } from 'react';
import { usePrintStations } from '@hooks/print-stations';
import { StatusBadge } from '@skupreme/shared/components/shipnode';
import { ToastMessage } from '@utility/toast';
import { LoaderBar } from '@components/LoaderBar';
import { ErrorMessageConstant } from '@utility/constants/common';
import ShippingLabelService from '@utility/services/shipping-label';
import { CenterDropdownStyle } from '@elements/Dropdown';
import BatchOrdersService from '@utility/services/batch-order';
import { StyledCheckbox } from '@elements/Common';
import { AppModal } from '@elements/Modal';
import { useAuth } from '@contexts/auth';
import { useDebouncedEffect } from '@hooks/debounceAsync';

const PrintType = {
  download: {
    value: 'download',
    text: 'Download',
  },
  view: {
    value: 'view',
    text: 'View',
  },
  print: {
    value: 'print',
    text: 'Print',
  },
};

const printSchema = yup.object().shape({
  defaultOption: yup.boolean(),
  option: yup.string().required(),
  computer: yup
    .string()
    .when('$schemeData', (schemeData, schema) =>
      schemeData.option !== PrintType.print.value
        ? schema.optional()
        : schema.required('Computer is required field'),
    ),
  printer: yup
    .string()
    .when('$schemeData', (schemeData, schema) =>
      schemeData.option !== PrintType.print.value
        ? schema.optional()
        : schema.required('Print is required field'),
    ),
});

export function PrintLabelPopup({
  open,
  closeModal,
  name,
  item,
  updateBatchOrderPickList,
  printURL,
  generateLabelText = 'Generate Label',
  prints,
  autoNodePrint = false,
}) {
  const { batch, label, print, updatePreferences, preferencesLoading } =
    useAuth();

  const [autoPrintedOnce, setAutoPrintedOnce] = useState(false);

  useEffect(() => {
    if (!open) {
      setAutoPrintedOnce(false);
    }
  }, [open]);

  const [schemeData, setSchemaData] = useState({
    option: '',
  });
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    watch,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(printSchema),
    shouldUnregister: true,
    context: {
      schemeData,
    },
  });

  const { stations, loading } = usePrintStations(open);
  const [printing, setPrinting] = useState(false);
  // @ts-expect-error - TS2769: No overload matches this call.
  const [option, computer] = watch(['option', 'computer']);
  const [step, setStep] = useState(0);
  const [paperType, setPaperType] = useState(null);
  const [batchPreference, setBatchPreference] = useState(false);
  const [url, setUrl] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);

  useEffect(() => {
    if (printURL || CommonUtility.isValidArray(prints)) {
      setStep(1);
    } else {
      setStep(0);
    }
  }, [open, printURL, prints]);

  useEffect(() => {
    setSchemaData({
      option,
    });
  }, [option]);

  const options = useMemo(
    () => [
      {
        text: 'Download a Label',
        value: PrintType.download.value,
      },
      {
        text: 'View in Browser',
        value: PrintType.view.value,
      },
      {
        text: 'Print via Shipnode',
        value: PrintType.print.value,
      },
    ],
    [],
  );

  const paperOptions = useMemo(
    () => [
      {
        text: 'Packing List and Label',
        value: 'no-saver',
      },
      {
        text: 'Condensed Packing List and Label',
        value: 'saver',
      },
      {
        text: 'Label Only',
        value: 'super-saver',
      },
    ],
    [],
  );

  /* Paired ShipNode stations, with the Settings tab's status lights. */
  const computerList = useMemo(
    () =>
      stations
        .filter(station => station.printers.length > 0)
        .map(station => ({
          text: station.name,
          content: (
            <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
              <span>{station.name}</span>
              <StatusBadge status={station.status} />
            </div>
          ),
          value: station.id,
          disabled: !station.online,
        })),
    [stations],
  );

  const printerList = useMemo(() => {
    const station = stations.find(x => x.id === (computer as string));
    if (!computer || !station) {
      return [];
    }
    return station.printers
      .map(printer => ({
        text: printer.name,
        content: (
          <div className="sk:flex sk:items-center sk:justify-between sk:gap-2">
            <span>{printer.name}</span>
            <StatusBadge status={printer.status} />
          </div>
        ),
        value: printer.id,
        disabled: !printer.online,
      }))
      .sort((a, b) => Number(a.disabled) - Number(b.disabled));
  }, [stations, computer]);

  useDebouncedEffect(
    () => {
      if (open && !preferencesLoading) {
        setBatchPreference(!!batch?.save_paper);
        // @ts-expect-error - TS2345: Argument of type 'string | null' is not assignable to parameter of type 'SetStat...
        setPaperType(batch?.save_paper || null);

        let t = {};
        // @ts-expect-error - TS18048: 'label' is possibly 'undefined'.
        if (label.output) {
          t = {
            // @ts-expect-error - TS18048: 'label' is possibly 'undefined'.
            option: label.output,
            // @ts-expect-error - TS18048: 'label' is possibly 'undefined'.
            defaultOption: !!label.output,
          };
        }
        if (print && computerList.some(x => x.value === print?.computer)) {
          t = {
            ...t,
            ...(print || {}),
          };
        }
        reset(t);
      }
    },
    [open, label, batch, print, preferencesLoading],
    500,
  );

  useDebouncedEffect(
    () => {
      if (!open) return;

      if (option === PrintType.print.value && autoNodePrint) {
        // @ts-expect-error - TS18048: 'print' is possibly 'undefined'.
        if (print.computer && print.printer) {
          handleSubmit(onSubmit)();
        }
      }
    },
    [option, autoNodePrint],
    200,
  );
  const printLabel = async (computerId, printerId) => {
    let successfull = 0;
    try {
      if (autoPrintedOnce) {
        return;
      }

      setAutoPrintedOnce(true);
      setPrinting(true);

      if (CommonUtility.isValidArray(prints)) {
        for (let i = 0; i < prints.length; i += 1) {
          await ShippingLabelService.print({
            message: new Date().toString(),
            computer_id: computerId,
            printer_id: printerId,
            // @ts-expect-error - TS2571: Object is of type 'unknown'.
            url: prints[i].url,
          });
          successfull += 1;
        }
      } else {
        await ShippingLabelService.print({
          message: new Date().toString(),
          computer_id: computerId,
          printer_id: printerId,
          url: printURL || url,
        });
      }

      ToastMessage.success('Printed successfully', '');

      setTimeout(() => {
        close();
      }, 100);
    } catch (error) {
      if (CommonUtility.isValidArray(prints) && successfull > 0) {
        ToastMessage.warning(
          `Printed ${successfull} out of ${prints.length} successfully`,
        );
      } else {
        ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
      }
    } finally {
      setPrinting(false);
    }
  };

  const openInBrowser = url => {
    const popup = window.open(url, '_blank');
    if (!popup) {
      ToastMessage.error(
        'Popup was blocked. Allow popups for this site and try again.',
      );
      return false;
    }

    popup.focus();
    return true;
  };

  const close = () => {
    ToastMessage.clear();
    closeModal();
  };

  const onSubmit = async formData => {
    if (formData.defaultOption) {
      const reqData = {
        label: {
          output: formData.option,
        },
      };
      if (formData.option === PrintType.print.value) {
        // @ts-expect-error - TS2339: Property 'print' does not exist on type '{ label: { output: any; }; }'.
        reqData.print = {
          computer: formData.computer,
          printer: formData.printer,
        };
      }
      updatePreferences(reqData);
    }
    if (formData.option === PrintType.download.value) {
      if (CommonUtility.isValidArray(prints)) {
        prints.forEach(print => {
          CommonUtility.downloadFile(
            // @ts-expect-error - TS18046: 'print' is of type 'unknown'.
            print.url,
            // @ts-expect-error - TS18046: 'print' is of type 'unknown'.
            print.name || `${+new Date()}.pdf`,
          );
        });
      } else {
        CommonUtility.downloadFile(
          printURL || url,
          name || `${+new Date()}.pdf`,
        );
      }

      setTimeout(() => {
        close();
      }, 100);
    } else if (formData.option === PrintType.view.value) {
      let opened = true;
      if (CommonUtility.isValidArray(prints)) {
        for (const print of prints) {
          // @ts-expect-error - TS18046: 'print' is of type 'unknown'.
          if (!openInBrowser(print.url)) {
            opened = false;
            break;
          }
        }
      } else {
        opened = openInBrowser(printURL || url);
      }
      if (opened) {
        setTimeout(() => {
          close();
        }, 100);
      }
    } else {
      await printLabel(formData.computer, formData.printer);
    }
  };

  const fetchUrl = async () => {
    try {
      if (batchPreference) {
        updatePreferences({
          batch: {
            save_paper: paperType,
          },
        });
      }
      setPopupLoading(true);
      const result = await BatchOrdersService.pickList({
        batch_id: item._id,
        email: true,
        save_paper: paperType,
      });

      setStep(1);
      setUrl(result.data);
      updateBatchOrderPickList(result.data, item._id);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setPopupLoading(false);
    }
  };

  return (
    <AppModal
      open={open}
      closeModal={() => close()}
      size="mini"
      title={step === 0 ? 'Select Batch Paper' : generateLabelText}
    >
      {(printing || popupLoading) && (
        <LoaderBar content={popupLoading ? 'Loading' : 'Printing Label'} />
      )}
      {step === 0 ? (
        <>
          <MuteLabel>Batch Paper</MuteLabel>
          <CustomField>
            <CenterDropdownStyle
              aria-label="Batch Paper"
              options={paperOptions}
              selection
              value={paperType}
              onChange={(e, { value }) => setPaperType(value)}
            />
          </CustomField>
          <CustomField>
            <StyledCheckbox
              checked={batchPreference}
              label="Save Preferences"
              onChange={(_, { checked }) => setBatchPreference(checked)}
            />
          </CustomField>
          <div className="sk:mt-4 sk:flex sk:items-center sk:justify-end sk:gap-3">
            <HoverBorderButton onClick={() => close()}>
              Cancel
            </HoverBorderButton>
            <StyledButton className="flex-btn" onClick={() => fetchUrl()}>
              Pick & Ship
              <ArrowRight className="sk:ml-2" />{' '}
            </StyledButton>
          </div>
        </>
      ) : (
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <DropdownFormField
            required
            options={options}
            name="option"
            error={errors.option}
            hint={errors?.option?.message}
            label="Action"
            control={control}
            sorted={false}
          />
          {option === PrintType.print.value && (
            <>
              <DropdownFormField
                options={computerList}
                name="computer"
                loading={loading}
                error={errors.computer}
                hint={errors?.computer?.message}
                label="Select a Computer"
                control={control}
                required
              />
              <DropdownFormField
                options={printerList}
                name="printer"
                loading={loading}
                error={errors.printer}
                hint={errors?.printer?.message}
                label="Select a Device"
                control={control}
                required
              />
            </>
          )}
          <CheckboxFormField
            control={control}
            name="defaultOption"
            text="Remember settings for this device"
          />
          <div className="sk:mt-4 sk:flex sk:items-center sk:justify-end sk:gap-3">
            <HoverBorderButton type="button" onClick={() => close()}>
              Cancel
            </HoverBorderButton>
            <StyledButton type="submit">
              {/* @ts-expect-error - TS2339: Property 'text' does not exist on type 'never'. */}
              {PrintType[option]?.text || 'Create'}
            </StyledButton>
          </div>
        </form>
      )}
    </AppModal>
  );
}
